import type { User } from "@domain/user";
import type { IUsers, RegisterInput } from "@infra/interfaces/auth/IUsers";
import type { ITokenStore } from "@infra/interfaces/storage/ITokenStore";
import type { IImpersonationStore } from "@infra/interfaces/storage/IImpersonationStore";
import { type Result, ok, err } from "@domain/Result";

// The login/register flows share the same shape: success returns a User; the
// known failure modes are an unauthenticated/unknown user and a bad request
// shape (e.g. duplicate email on register). Anything else throws.
export type LoginError = "invalid_credentials" | "user_not_found" | "user_not_registered";
export type RegisterError = "email_already_exists" | "invalid_input";

export interface AuthUseCaseDeps {
	userApi: IUsers;
	tokenStore: ITokenStore;
	impersonationStore: IImpersonationStore;
}

/**
 * Auth use cases bundled together because they share dependencies (a backend
 * `IUsers`, the `ITokenStore`, and the `IImpersonationStore`) and operate on
 * the same conceptual subject — the active session.
 *
 * The browser-wired singleton is exported from `./index` as `authUseCase`.
 * Tests construct their own `AuthUseCase` with mock ports; the CLI would do
 * the same with CLI-friendly adapters.
 */
export class AuthUseCase {
	constructor(private readonly deps: AuthUseCaseDeps) {}

	/**
	 * Authenticate a user. Translates well-known backend error messages into
	 * typed `LoginError` variants; lets transport/5xx errors throw.
	 */
	async login(username: string, password: string): Promise<Result<User, LoginError>> {
		try {
			const user = await this.deps.userApi.login(username, password);
			if (!user) return err("invalid_credentials");
			this.persistAuthTokens(user.token);
			return ok(user);
		} catch (e: unknown) {
			const variant = mapLoginError(e);
			if (variant !== null) return err(variant);
			throw e;
		}
	}

	/**
	 * Register a new user. The returned User may have `token === NEW_USER_TOKEN`
	 * (still pending activation); callers are responsible for routing accordingly.
	 *
	 * Auth tokens are persisted only when the registration is complete (the user
	 * has a real token, not the placeholder `NEW_USER_TOKEN`).
	 */
	async register(input: RegisterInput): Promise<Result<User, RegisterError>> {
		try {
			const user = await this.deps.userApi.register(input);
			if (!user) return err("invalid_input");
			if (user.token && user.token !== "new_user") {
				this.persistAuthTokens(user.token);
			}
			return ok(user);
		} catch (e: unknown) {
			if (isHttpStatus(e, 409)) return err("email_already_exists");
			throw e;
		}
	}

	/**
	 * Persist the three auth-token slots through the `ITokenStore` port.
	 * - `idToken` (key: `rogo_id_token`) authenticates subsequent API requests.
	 * - `jwt` is read by the React app to decide "is the user logged in."
	 * - `refreshToken` is reset to empty so a stale token from a prior session
	 *   doesn't linger.
	 */
	private persistAuthTokens(token: string): void {
		this.deps.tokenStore.setIdToken(token);
		this.deps.tokenStore.setJwt(token);
		this.deps.tokenStore.setRefreshToken("");
	}

	/**
	 * Clear every token slot and any active impersonation. Targeted (only
	 * keys we own) — does not touch other persisted UI state (saved views,
	 * layouts).
	 */
	logout(): void {
		this.deps.tokenStore.clearAll();
		this.deps.impersonationStore.clear();
	}
}

// ---- internal helpers ----

function mapLoginError(e: unknown): LoginError | null {
	const message = extractMessage(e);
	if (!message) return null;
	switch (message) {
		case "Incorrect username or password":
			return "invalid_credentials";
		case "User does not exist":
			return "user_not_found";
		case "User is not registered":
			return "user_not_registered";
		default:
			return null;
	}
}

function extractMessage(e: unknown): string | undefined {
	if (typeof e !== "object" || e === null) return undefined;
	const err = e as { response?: { data?: { detail?: string } }; message?: string };
	return err.response?.data?.detail ?? err.message;
}

function isHttpStatus(e: unknown, status: number): boolean {
	if (typeof e !== "object" || e === null) return false;
	const err = e as { response?: { status?: number } };
	return err.response?.status === status;
}
